import React from "react";

// seo
import Seo from "../components/Seo";

const PrivacyPolicyPage = () => {
  return (
    <div>
      <Seo
        title="Privacy Policy | Twin Health India"
        description="See how Twin Health India keeps your personal and health data safe, and how we use and protect it, all explained in simple, plain words."
      />

      <div className="container py-5">
        <div className="row">
          <div className="col-12">
            <h1 className="text-center mb-4" style={{ fontWeight: "700" }}>
              TWIN HEALTH — TERMS OF USE & PRIVACY POLICY
            </h1>

            <div className="mb-4">
              <p className="mb-2">
                <strong>Effective Date:</strong> October 8, 2025
              </p>
            </div>

            <section className="mb-5">
              <h2 className="h4 mb-3" style={{ fontWeight: "600" }}>
                1. INTRODUCTION
              </h2>
              <p className="mb-3">
                <strong>1.1</strong> These Terms of Use and Privacy Policy
                (collectively referred to as the "Terms") constitute a legally
                binding agreement between you ("User", "you", or "your") and
                Twin Digital Services India Private Limited, a company
                incorporated under the Companies Act, 2013, having its
                registered office in India ("Twin India"), and its affiliates,
                including Twin Health, Inc. (collectively referred to as "Twin
                Health", "we", "us", or "our").
              </p>
              <p className="mb-3">
                <strong>1.2</strong> These Terms govern your access to and use
                of Twin Health's digital health platforms, mobile application,
                website, and related connected health monitoring and telehealth
                services (the "Services"). By accessing or using the Services,
                you acknowledge that you have read, understood, and agree to be
                bound by these Terms and all applicable laws and regulations. If
                you do not agree, you must discontinue use of the Services
                immediately.
              </p>
              <p className="mb-3">
                <strong>1.3</strong> Twin Health operates in compliance with the
                applicable laws of India, including the Digital Personal Data
                Protection Act, 2023 (DPDP Act), Telemedicine Practice
                Guidelines, 2020, and, where applicable, U.S. HIPAA standards
                for Protected Health Information (PHI).
              </p>
              <p className="mb-3">
                <strong>This document is structured as follows:</strong>
              </p>
              <ul className="mb-3">
                <li>a) Medical Policy & Consent</li>
                <li>b) Twin Service Policy</li>
                <li>c) Privacy Policy</li>
                <li>d) Terms of Use</li>
                <li>e) Annexures</li>
              </ul>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3" style={{ fontWeight: "600" }}>
                2. MEDICAL POLICY & CONSENT
              </h2>
              <h3 className="h5 mb-3" style={{ fontWeight: "600" }}>
                2.1 Medical Consent (Telemedicine)
              </h3>
              <p className="mb-3">
                By signing the medical consent (digitally or physically), you
                acknowledge and agree as follows:
              </p>
              <p className="mb-3">
                <strong>2.1.1 Language and Understanding:</strong> The nature,
                purpose, and potential risks of the procedures and
                teleconsultation services have been explained to me in a
                language I understand: ____________________.
              </p>
              <p className="mb-3">
                <strong>2.1.2 Consent to Telehealth:</strong> I hereby consent
                to receive remote medical consultation and health management
                services through Twin Health's digital platforms, including all
                data processing and sharing as per the Privacy Policy.
              </p>
              <p className="mb-3">
                <strong>2.1.3 Scope of Consent:</strong> I authorize the
                attending healthcare professional(s) to perform or attempt
                additional procedures or interventions deemed necessary in their
                professional judgment during the course of treatment.
              </p>
              <p className="mb-3">
                <strong>2.1.4 Alternatives and Risks:</strong> I acknowledge
                that alternative treatment options, potential benefits, and
                reasonably foreseeable risks have been explained to me. I have
                received explanations and understand that the proposed
                telehealth treatment has certain material risks/complications
                and I have been provided with their quite information about the
                same, including that there are other undefined, unanticipated,
                unexplainable risks/complications that may occur during or after
                the proposed treatment/intervention.
              </p>
              <p className="mb-3">
                <strong>2.1.5 No Guarantee:</strong> I understand after full
                explanation and the opportunity for discussion that despite best
                efforts there can be no assurance about the results or outcome
                of the proposed treatment/intervention. I confirm that I have
                not been given any guarantee or warranty about the results or
                outcome of the proposed treatment/intervention.
              </p>
              <p className="mb-3">
                <strong>2.1.6 Clarifications:</strong> I confirm that the
                Provider has answered all my questions to my satisfaction
                regarding the proposed treatment/intervention and the medical
                teams involved.
              </p>
              <p className="mb-3">
                <strong>2.1.7 Right to Second Opinion:</strong> I have been
                advised of my own election to seek and obtain a second opinion
                from another doctor regarding the proposed
                treatment/intervention.
              </p>
              <p className="mb-3">
                <strong>2.1.8 Right to Withdraw:</strong> I understand that I
                have the right to refuse treatment or to withdraw my consent at
                any time. I understand that if I do refuse treatment, the
                consequences of my decision have been explained to me. I confirm
                that after explanation, discussion, counselling and disclosures
                I have had ample time to provide consent.
              </p>
              <p className="mb-3">
                <strong>2.1.9 Voluntary Consent:</strong> This consent has been
                given voluntarily, without coercion or undue influence.
              </p>
              <p className="mb-3">
                <strong>Note:</strong> All teleconsultations shall be conducted
                in accordance with the Telemedicine Practice Guidelines, 2020
                and any subsequent amendments issued by the Medical Council of
                India or relevant authority.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3" style={{ fontWeight: "600" }}>
                3. TWIN SERVICE POLICY
              </h2>

              <h3 className="h5 mb-3" style={{ fontWeight: "600" }}>
                3.1 Devices and Sensors
              </h3>
              <p className="mb-3">
                <strong>3.1.1</strong> Users are responsible for procuring and
                maintaining approved third-party devices (such as CGM sensors,
                fitness trackers, BP monitors, and body composition scales)
                required for the program.
              </p>
              <p className="mb-3">
                <strong>3.1.2</strong> Twin Health shall not be liable for any
                inaccuracy, malfunction, or operational failure of third-party
                devices.
              </p>
              <p className="mb-3">
                <strong>3.1.3</strong> Continuous Glucose Monitor (CGM) users
                are required to upload readings daily via the designated portal
                or email (e.g., remote@twinhealth.com). Approximately six (6)
                CGM patches are expected to be used annually unless medically
                indicated otherwise.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                3.2 Laboratory Work and Diagnostics
              </h3>
              <p className="mb-3">
                <strong>3.2.1</strong> Blood and diagnostic tests shall be
                conducted at baseline and at approximately 90-day intervals
                thereafter.
              </p>
              <p className="mb-3">
                <strong>3.2.2</strong> Users shall ensure that all laboratory
                results are shared with Twin Health to facilitate clinical
                monitoring.
              </p>
              <p className="mb-3">
                <strong>3.2.3</strong> A representative list of test parameters
                is provided in Annexure A.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                3.3 Virtual Assistance and Support
              </h3>
              <p className="mb-3">
                <strong>3.3.1</strong> Routine technical and sensor-related
                assistance will be rendered by the Twin Service Assistant (TSA)
                via virtual channels.
              </p>
              <p className="mb-3">
                <strong>3.3.2</strong> Any escalation or unresolved issue may be
                directed to care@twinhealth.com.
              </p>

              <p className="mb-3 mt-4">
                <strong>DISCLAIMER:</strong> I understand this program is a No
                Refund Policy and I agree that I will fully comply to the
                program guidelines and instructions for my journey
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3" style={{ fontWeight: "600" }}>
                4. Twin Health Privacy Policy
              </h2>
              <p className="mb-3">
                <strong>Effective Date:</strong> July 07, 2023
                <br />
                <strong>Last Modified:</strong> July 07, 2023
              </p>
              <p className="mb-3">
                Twins Digital Services India Private limited otherwise called as
                "Twin Health", and its related entities (hereinafter referred to
                as "Twin Health", "We", "Us") are committed to protecting the
                privacy, confidentiality, and security of all personal and
                sensitive information collected through our mobile application,
                therapy, and other health-related services (collectively, the
                "Services"). This Privacy Policy ("Policy") outlines the manner
                in which we collect, process, store, use, and share personal
                information from users in compliance with the Digital Personal
                Data Protection Act, 2023 (DPDP Act), the Information Technology
                Act, 2000 (and its amendments), as well as applicable
                international regulations such as HIPAA in the United States.
              </p>
              <p className="mb-3">
                By accessing or using the Services, you acknowledge that you
                have read, understood, and consent to the practices described
                herein. In case you do not agree to the terms of this Policy,
                you should immediately cease using the Services. This Policy is
                subject to updates, and continued use of the Services after
                changes are posted constitutes acceptance of such changes.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.1 Scope of this Privacy Policy:
              </h3>
              <p className="mb-3">
                This Policy governs the collection, use, and disclosure of
                personal information through the App and Services provided by
                Twin Health. The Policy does not apply to information collected
                through other websites, third-party services, or external
                applications linked to or accessible from our App. Twin Health
                also maintains a separate Notice of Privacy Practices regarding
                protected health information ("PHI"), which will prevail in case
                of any inconsistency with this Policy.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.2 Information We Collect:
              </h3>
              <p className="mb-3">
                Twin Health collects personal and sensitive information that is
                necessary for the provision of our Services. The information may
                be collected directly from you, from third parties, or
                automatically through your interaction with our App.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.3 Information You Provide:
              </h3>
              <p className="mb-3">
                When you register for the Services, we collect information
                necessary to create and maintain your account. This includes
                your name, date of birth, contact details (such as email
                address, telephone number, and addresses), password, health and
                medical information, insurance details, racial or ethnic origin,
                medical history including diagnoses, symptoms, and other
                relevant personal details. You may provide additional
                information voluntarily, including health reports, lifestyle
                information, or feedback to improve the Services.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.4 Information from Third Parties
              </h3>
              <p className="mb-3">
                In certain cases, Twin Health may collect information about you
                from third parties such as your employer, health insurance
                providers, or authorized business partners. This information is
                used solely for the purpose of delivering and enhancing the
                Services. Twin Health ensures that any such collection is
                compliant with the applicable Indian and international privacy
                laws and that appropriate contractual or legal safeguards are in
                place.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.5 Automatic Collection of Information
              </h3>
              <p className="mb-3">
                Twin Health may automatically collect certain data when you
                access or use the App. This may include your device type,
                operating system, mobile network details, IP address,
                geolocation, activity logs, and usage patterns. Cookies and
                similar tracking technologies may be used to improve user
                experience, analyse App usage, and personalize content. We may
                also utilize third-party analytics tools, including Google
                Analytics and Mix panel, which may collect information on your
                interactions with the Services. Twin Health does not currently
                respond to "Do Not Track" (DNT) browser signals.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.6 Use of Information:
              </h3>
              <p className="mb-3">
                The information collected is used exclusively for legitimate
                purposes required to provide the Services and comply with legal
                obligations. Twin Health uses your personal information to:
              </p>
              <p className="mb-3">
                <strong>4.6.1</strong> Deliver the Services requested and
                respond to inquiries or support requests;
              </p>
              <p className="mb-3">
                <strong>4.6.2</strong> Maintain accurate records for billing,
                payment, and contractual compliance;
              </p>
              <p className="mb-3">
                <strong>4.6.3</strong> Notify you of updates, changes, or
                enhancements to the Services or App;
              </p>
              <p className="mb-3">
                <strong>4.6.4</strong> Comply with legal and regulatory
                requirements, including those under Indian law and HIPAA for
                U.S.-based users;
              </p>
              <p className="mb-3">
                <strong>4.6.5</strong> Conduct research, statistical analysis,
                and quality improvement initiatives in a de-identified or
                aggregated form;
              </p>
              <p className="mb-3">
                <strong>4.6.6</strong> Communicate with insurers, employers, or
                other authorized parties to facilitate healthcare and benefits
                administration;
              </p>
              <p className="mb-3">
                <strong>4.6.7</strong> Ensure the security of Services, prevent
                fraud, and detect unauthorized access.
              </p>
              <p className="mb-3">
                For PHI, Twin Health strictly adheres to HIPAA regulations and
                will only use or disclose such information as permitted under
                the law or with your explicit consent. PHI will not be used for
                marketing purposes without your prior consent.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.7. Sharing and Disclosure of Information:
              </h3>
              <p className="mb-3">
                Twin Health does not sell your personal information. Disclosure
                is limited to purposes necessary to deliver the Services or as
                required by law. Personal information may be shared with:
              </p>
              <p className="mb-3">
                <strong>4.7.1</strong> Your employer or authorized service
                providers for the purpose of delivering the Services;
              </p>
              <p className="mb-3">
                <strong>4.7.2</strong> Business associates or contractors
                providing IT, infrastructure, analytics, billing, or marketing
                support, subject to binding confidentiality agreements;
              </p>
              <p className="mb-3">
                <strong>4.7.3</strong> Legal or regulatory authorities in
                compliance with Indian laws, court orders, or to protect the
                rights, property, or safety of Twin Health, its users, or the
                public;
              </p>
              <p className="mb-3">
                <strong>4.7.4</strong> Third parties in connection with
                corporate transactions, such as mergers, acquisitions,
                restructuring, or asset sales;
              </p>
              <p className="mb-3">
                <strong>4.7.5</strong> De-identified or aggregated data may be
                shared for research, analytics, product development, or
                scientific studies.
              </p>
              <p className="mb-3">
                Any disclosure is governed by appropriate contracts or legal
                obligations ensuring compliance with applicable privacy laws.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.8 Cross-Border Data Transfers
              </h3>
              <p className="mb-3">
                As Twin Health operates internationally, your information may be
                stored or processed in countries such as the United States or
                Singapore. Twin Health ensures such transfers comply with
                applicable laws, including the DPDP Act, IT Act, and HIPAA, and
                implements safeguards such as Standard Contractual Clauses
                (SCCs) and binding Data Processing Agreements with third
                parties. By using the Services, you consent to such cross-border
                transfers.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.9 Your Rights Under Indian Law
              </h3>
              <p className="mb-3">
                Under the DPDP Act, 2023, and related Indian privacy
                regulations, you have the following rights:
              </p>
              <p className="mb-3">
                <strong>4.9.1 Right to Access and Portability:</strong> You may
                request access to your personal information and obtain a copy in
                a portable format.
              </p>
              <p className="mb-3">
                <strong>4.9.2 Right to Correction and Update:</strong> You may
                request correction of any inaccurate, incomplete, or outdated
                information.
              </p>
              <p className="mb-3">
                <strong>4.9.3 Right to Erasure:</strong> Subject to legal and
                contractual obligations, you may request deletion of your
                personal information.
              </p>
              <p className="mb-3">
                <strong>4.9.4 Right to Restriction/Objection:</strong> You may
                restrict or object to processing for specific purposes,
                including direct marketing.
              </p>
              <p className="mb-3">
                <strong>4.9.5 Right to Withdraw Consent:</strong> You may
                withdraw consent for processing where applicable.
              </p>
              <p className="mb-3">
                <strong>4.9.6 Right to Complaint:</strong> You may submit
                complaints regarding data privacy practices to Twin Health or
                the appropriate regulatory authority.
              </p>
              <p className="mb-3">
                Requests for exercising these rights can be sent to
                PrivacyIndia@TwinHealth.com.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.10 Data Retention:
              </h3>
              <p className="mb-3">
                Twin Health retains personal information only as long as
                necessary to provide Services, comply with legal obligations,
                resolve disputes, and enforce agreements. After the retention
                period, personal data is either securely deleted or anonymized.
                Retention periods are documented and reviewed in accordance with
                DPDP Act and IT Act compliance guidelines.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.11 Security of Information:
              </h3>
              <p className="mb-3">
                Twin Health has implemented reasonable technical,
                administrative, and physical safeguards to protect personal
                information against unauthorized access, disclosure, alteration,
                or destruction. Security measures include encryption (SSL/TLS,
                AES-256), role-based access, multi-factor authentication, audit
                logging, and secure data storage. Despite these measures, no
                system is completely secure. Transmission of information over
                the Internet carries inherent risks, and users are encouraged to
                take appropriate precautions.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.12 Children's Privacy:
              </h3>
              <p className="mb-3">
                The Services are not intended for children under 18 years of
                age. Twin Health does not knowingly collect personal information
                from children. Any information inadvertently collected from a
                minor will be deleted promptly upon discovery. Parents or
                guardians may contact PrivacyIndia@TwinHealth.com to request
                deletion or correction of such information.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.13 Third-Party Websites and Advertisers
              </h3>
              <p className="mb-3">
                The App may contain links to third-party websites or services,
                which are not under Twin Health's control. Twin Health is not
                responsible for the privacy practices of such third parties.
                Third-party advertisers may use cookies, web beacons, or other
                tracking technologies to deliver interest-based advertising. No
                personal health information from the App or Apple Health is
                shared with advertisers.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.14 Changes to this Privacy Policy
              </h3>
              <p className="mb-3">
                Twin Health may amend this Policy at any time to reflect changes
                in legal requirements, services, or business practices.
                Amendments will be effective immediately upon posting on the
                App. Users are responsible for reviewing this Policy
                periodically. Continued use of the Services constitutes
                acceptance of such changes.
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.15 Contact Information
              </h3>
              <p className="mb-3">
                For queries, complaints, or to exercise your rights under this
                Policy, please contact:
              </p>
              <p className="mb-3">
                <strong>Privacy/Data Protection Office</strong>
                <br />
                <strong>Email:</strong> PrivacyIndia@TwinHealth.com
                <br />
                <strong>Address:</strong> Twin Digital Services India Pvt. Ltd.,
                Chennai, India
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.16 Governing Law and Jurisdiction
              </h3>
              <p className="mb-3">
                This Policy and any dispute arising out of its application shall
                be governed by the laws of India, including the DPDP Act, IT
                Act, and other applicable regulations. For U.S.-based users,
                HIPAA and relevant federal or state laws shall govern the use of
                PHI. Disputes will be first attempted to be resolved informally
                with the Privacy Office. Legal proceedings, if necessary, will
                be under the exclusive jurisdiction of courts in Bengaluru,
                India (for Indian users) and Santa Clara, California (for U.S.
                users).
              </p>

              <h3 className="h5 mb-3 mt-4" style={{ fontWeight: "600" }}>
                4.17 Acknowledgment
              </h3>
              <p className="mb-3">
                By accessing or using Twin Health Services, you acknowledge that
                you have read, understood, and agreed to the terms and practices
                described in this Privacy Policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
